import { CdkScrollable } from '@angular/cdk/overlay';
import { CdkVirtualScrollableElement } from '@angular/cdk/scrolling';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { AuthenticationStore } from 'authentication-data';
import { CART_CHECKOUT_HANDLER, CartComponentStore, SavedCartsStore, SavedPartListsStore } from 'cart-data';
import { ConfirmDialogResponse, DialogService, DrawerComponent } from 'common-ui';
import { ClickOutsideDirective, CurrencyHtmlPipe } from 'common-util';
import { CurrentCustomerStore } from 'customers-data';
import { firstValueFrom } from 'rxjs';
import { CartItemResultsComponent } from '../item';
import { SaveCartAsListDialogComponent } from './save/save-cart-as-list-dialog';
import { SaveCartDialogComponent, SaveCartDialogData } from './save/save-cart-dialog';

@Component({
  selector: 'ideal-cart-menu',
  standalone: true,
  imports: [
    CartItemResultsComponent,
    CurrencyHtmlPipe,
    CdkScrollable,
    CdkVirtualScrollableElement,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
  ],
  templateUrl: './cart-menu.component.html',
  styleUrl: './cart-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartMenuComponent {
  private readonly _authStore = inject(AuthenticationStore);
  private readonly _currentCustomerStore = inject(CurrentCustomerStore);
  private readonly _cartStore = inject(CartComponentStore);
  private readonly _savedCartsStore = inject(SavedCartsStore);
  private readonly _savedPartListsStore = inject(SavedPartListsStore);
  private readonly _dialogService = inject(DialogService);
  private readonly _checkoutHandler = inject(CART_CHECKOUT_HANDLER);
  protected readonly cartItems = this._cartStore.cartItems;
  protected readonly cartItemCount = this._cartStore.cartItemCount;
  protected readonly cartPrices = this._cartStore.cartPrices;
  protected readonly cartSubTotalWithFees = this._cartStore.cartSubTotalWithFees;
  protected readonly cartSubTotal = this._cartStore.cartSubTotal;
  protected readonly cartSubTotalFees = this._cartStore.cartSubTotalFees;

  protected readonly isAuthenticated = this._authStore.isAuthenticated;
  protected readonly checkoutLabel = this._checkoutHandler.label;
  protected readonly checkoutBusy = this._checkoutHandler.busy;

  constructor() {
    this._cartStore.selectCart('main');
  }

  protected checkout(): Promise<void> {
    return this._checkoutHandler.checkout();
  }

  protected async promptSaveCart() {
    const ref = this._dialogService.dialog(SaveCartDialogComponent, {
      data: {
        customerNumber: this._currentCustomerStore.customerNumber(),
      } as SaveCartDialogData,
    });
    const result = (await firstValueFrom(ref.afterClosed())) as { action: 'new' | 'merge' | 'replace'; value: string };
    if (result) {
      switch (result.action) {
        case 'new':
          await this._savedCartsStore.saveCartAs(result.value);
          break;
        case 'replace':
          await this._savedCartsStore.replaceSavedCart(+result.value);
          break;
        case 'merge':
          await this._savedCartsStore.mergeToSavedCart(+result.value);
          break;
      }
    }
  }
  protected async promptSaveList() {
    const ref = this._dialogService.dialog(SaveCartAsListDialogComponent, {
      data: {
        customerNumber: this._currentCustomerStore.customerNumber(),
      } as SaveCartDialogData,
    });
    const result = (await firstValueFrom(ref.afterClosed())) as { action: 'new' | 'merge' | 'replace'; value: string };
    if (result) {
      switch (result.action) {
        case 'new':
          await this._savedPartListsStore.saveListAs(result.value);
          break;
        case 'replace':
          await this._savedPartListsStore.replaceSavedList(result.value);
          break;
        case 'merge':
          await this._savedPartListsStore.mergeToSavedList(result.value);
          break;
      }
    }
  }
  protected async promptClear() {
    const result = await this._dialogService.confirm('Are you sure you want to clear the cart?', 'Clear Cart', {
      cancelButtonHidden: true,
    });
    if (result === ConfirmDialogResponse.Positive) {
      this._cartStore.clearCart();
    }
  }
}
